/**
 * The dropzone and the file row (sheet 11 callouts 2 and 7; FR-006, FR-007,
 * FR-008, C-004; U.3, U.4, P.4; style guide §7.15; research §1.13, §1.15;
 * post-plan T3; post-tasks P2, E5).
 *
 * Every limit comes from the public layout document: `limits.admission_cap`,
 * `limits.bytes` and `limits.types` for Path A, and `pathBByteLimit` for Path
 * B. A wrong type or an oversize file is refused here, before any grant, with
 * the §7.15 error treatment. Without the document the dropzone is disabled with
 * register 16 and a Retry: nothing falls back to a number.
 *
 * Mission 9 WP14 T089 (51/9; FR-015; B.1; U11): with `billingRefusal` (the billing gate's reason,
 * passed by NewRun) the dropzone rejects drops and the file input, hides browse and states the reason
 * as an inline caption in place of the instructions.
 */
import { useId, useRef, useState, type DragEvent } from 'react'
import { AlertCircle, Upload, X } from 'lucide-react'

import { Band } from '@/components/trellis/Band'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { Button } from '@/components/ui/button'
import { cn } from '@/components/ui/cn'
import { Skeleton } from '@/components/ui/skeleton'
import type { RunPath } from '@/lib/api/runs'
import { Mono, bytes, bytesExact, count } from '@/lib/format'
import type { ProblemOverrides } from '@/lib/problems'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'

import type { UploadState } from './useUpload'

export interface DropzoneLimits {
  admission_cap: number
  bytes: number
  types: string[]
}

/**
 * The Path B byte limit: the admission cap times 2,048 bytes. The one place the
 * multiplier lives (DM-01M39TVF94JS6PXWP3T94RNCN0).
 */
export function pathBByteLimit(admissionCap: number): number {
  return admissionCap * 2048
}

/** Path B's content types (contracts/ui-api.md "New run"): a Metro 2 file in character format. */
export const PATH_B_TYPES: readonly string[] = ['text/plain', 'application/octet-stream']

const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const PATH_A_EXTENSIONS: Record<string, string> = { '.csv': 'text/csv', '.xlsx': XLSX }
/**
 * Path B mirrors the grant's `metro2Extensions` exactly
 * (backend/internal/service/upload/grant.go): `.dat`, `.txt`, `.metro2` and no
 * extension. Trellis's own Metro 2 downloads are `.metro2`. Anything else, a
 * spreadsheet or a document included, is refused here as the grant would.
 */
const PATH_B_EXTENSIONS: Record<string, string> = {
  '.dat': 'application/octet-stream',
  '.txt': 'text/plain',
  '.metro2': 'application/octet-stream',
  '': 'application/octet-stream',
}

/** The extension as Go's `path.Ext` gives it, lower-cased: from the last dot, dot included; '' without one. */
function extension(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot < 0 ? '' : name.slice(dot).toLowerCase()
}

/**
 * The content type the grant declares (post-tasks E5), or null when the file is
 * refused. The browser's `file.type` is not trusted: Chromium reports '' for
 * `.metro2`, for a file with no extension and often for a CSV. Path A maps the
 * extension to a type the layout document serves, so the document still
 * governs. Path B maps the grant's four extensions; packed format is the
 * server's refusal (U.4).
 */
export function resolveContentType(file: File, path: RunPath, types: readonly string[]): string | null {
  const ext = extension(file.name)
  if (path === 'generate') {
    const mapped = PATH_A_EXTENSIONS[ext]
    return mapped !== undefined && types.includes(mapped) ? mapped : null
  }
  return PATH_B_EXTENSIONS[ext] ?? null
}

function acceptFor(path: RunPath, types: readonly string[]): string {
  if (path === 'analyze') return ['.dat', '.txt', '.metro2', ...PATH_B_TYPES].join(',')
  const exts = Object.entries(PATH_A_EXTENSIONS)
    .filter(([, type]) => types.includes(type))
    .map(([ext]) => ext)
  return [...exts, ...types].join(',')
}

export type DropzoneState = 'loading' | 'ready' | 'unavailable'

export interface DropzoneProps {
  path: RunPath
  limits: DropzoneLimits | undefined
  state: DropzoneState
  onRetry: () => void
  onFile: (file: File, contentType: string) => void
  /** The grant refused the file (415 or 413): the same refusal, from the layout values. */
  refused?: boolean | undefined
  /** A 400 `validation-failed` on `filename`, rendered inline under the dropzone. */
  fieldError?: string | undefined
  /** The billing gate's reason while uploads are refused (useBillingGate; B.1). */
  billingRefusal?: string | undefined
}

export function Dropzone({ path, limits, state, onRetry, onFile, refused = false, fieldError, billingRefusal }: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const reasonId = useId()
  const [dragOver, setDragOver] = useState(false)
  const [localRefusal, setLocalRefusal] = useState(false)
  const ready = state === 'ready' && limits !== undefined && billingRefusal === undefined

  const types = path === 'analyze' ? PATH_B_TYPES : (limits?.types ?? [])
  const byteLimit = limits === undefined ? 0 : path === 'analyze' ? pathBByteLimit(limits.admission_cap) : limits.bytes
  const showRefusal = ready && (localRefusal || refused)

  function take(file: File | undefined) {
    if (!file || !ready) return
    const contentType = resolveContentType(file, path, limits.types)
    if (contentType === null || file.size > byteLimit) {
      setLocalRefusal(true)
      return
    }
    setLocalRefusal(false)
    onFile(file, contentType)
  }

  function onDragOver(event: DragEvent<HTMLDivElement>) {
    event.preventDefault()
    if (ready) setDragOver(true)
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault()
    setDragOver(false)
    take(event.dataTransfer.files[0])
  }

  const browse = ready ? (
    <Button variant="link" className="text-body" onClick={() => inputRef.current?.click()}>
      browse
    </Button>
  ) : (
    // The band below states the reason; the button points at it (§7.1, §11).
    <Button variant="link" className="text-body" disabled aria-describedby={reasonId}>
      browse
    </Button>
  )

  return (
    <div>
      <div
        data-dropzone=""
        onDragOver={onDragOver}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={cn(
          'flex min-h-40 flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-navy-300 bg-navy-50 p-6 text-center',
          dragOver && 'border-[var(--color-accent)] bg-[var(--color-accent-50)]',
          showRefusal && 'border-[var(--color-error-600)] bg-[var(--color-error-50)]',
        )}
      >
        <Upload aria-hidden className="size-8 text-navy-500" />
        {state === 'loading' ? (
          <>
            <Skeleton className="h-5 w-64" />
            <Skeleton className="h-4 w-48" />
          </>
        ) : billingRefusal !== undefined ? (
          <p className="text-caption text-navy-600">{billingRefusal}</p>
        ) : (
          <>
            {path === 'generate' ? (
              <p className="text-body text-navy-950">Drop a CSV or XLSX here, or {browse}</p>
            ) : limits !== undefined ? (
              <p className="text-body text-navy-950">
                {PROVISIONAL_COPY.pathBDropzoneBody.render({
                  admissionCap: limits.admission_cap,
                  pathBBytes: pathBByteLimit(limits.admission_cap),
                })}{' '}
                {browse}
              </p>
            ) : null}
            {ready ? (
              <p className="text-caption text-navy-600">
                {/* owner decision 2026-09-26 (decision 7): Path B has its own caption, in base segments. */}
                {path === 'analyze'
                  ? PROVISIONAL_COPY.dropzoneCaptionPathB.render({ admissionCap: limits.admission_cap, bytes: byteLimit })
                  : PROVISIONAL_COPY.dropzoneCaption.render({ types, admissionCap: limits.admission_cap, bytes: byteLimit })}
              </p>
            ) : null}
            {showRefusal ? (
              <p role="alert" className="flex items-center gap-1 text-caption text-[var(--color-error-700)]">
                <AlertCircle aria-hidden className="size-4 shrink-0" />
                {path === 'analyze'
                  ? PROVISIONAL_COPY.dropzoneRefusalPathB.render({ bytes: byteLimit })
                  : PROVISIONAL_COPY.dropzoneRefusal.render({ types, bytes: byteLimit })}
              </p>
            ) : null}
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={limits === undefined ? undefined : acceptFor(path, limits.types)}
          disabled={!ready}
          onChange={(event) => {
            take(event.target.files?.[0])
            event.target.value = ''
          }}
        />
      </div>
      {state === 'unavailable' ? (
        <Band
          id={reasonId}
          kind="blocked"
          className="mt-2"
          action={
            <Button variant="secondary" size="sm" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          {copyText('layoutUnreachable')}
        </Band>
      ) : null}
      {fieldError !== undefined ? (
        <p className="mt-1.5 flex items-center gap-1 text-caption text-[var(--color-error-700)]">
          <AlertCircle aria-hidden className="size-4 shrink-0" />
          {fieldError}
        </p>
      ) : null}
    </div>
  )
}

export interface FileRowProps {
  filename: string
  sizeBytes: number
  /** The parsed count once validated; absent while validating. */
  recordCount?: number | undefined
  onRemove: () => void
}

/**
 * The file row that replaces the dropzone (§7.15): the mono filename, the size,
 * the parsed record count in mono (or "Validating…" while completion runs), and
 * a client-side remove (there is no delete route).
 */
export function FileRow({ filename, sizeBytes, recordCount, onRemove }: FileRowProps) {
  return (
    <div data-file-row="" className="flex items-center gap-4 rounded-lg border border-navy-200 bg-white px-4 py-3">
      <Mono className="min-w-0 flex-1 truncate text-body text-navy-950">{filename}</Mono>
      {/* mission 8 WP10 T055 (FR-020 A-L3; P4): the exact size as sr-only text; an aria-label on a role-less span is not exposed. */}
      <span className="text-body-sm text-navy-600">
        <span aria-hidden="true">{bytes(sizeBytes)}</span>
        <span className="sr-only">{bytesExact(sizeBytes)}</span>
      </span>
      {recordCount === undefined ? (
        <span aria-live="polite" className="text-body-sm text-navy-600">
          Validating…
        </span>
      ) : (
        <Mono data-record-count="" className="text-body-sm text-navy-950">
          {count(recordCount)}
        </Mono>
      )}
      <Button variant="ghost" size="sm" aria-label={`Remove ${filename}`} onClick={onRemove}>
        <X aria-hidden />
      </Button>
    </div>
  )
}

/**
 * The upload's own renderings (contracts/ui-api.md "New run" completion table).
 * Mismatch and scan refusals read "Upload the file again" (SEC.8); a 410 D.2 is
 * retention, rendered by the gate table, never the expired-grant sentence (C12).
 */
const UPLOAD_OVERRIDES: ProblemOverrides = {
  'upload-object-mismatch': { band: 'blocked', remedy: 'uploadAgain' },
  'upload-rejected-by-scan': { band: 'blocked', remedy: 'uploadAgain' },
}

/** P.4 on Path B, whose cap counts base segments (owner decision 2026-09-26, decision 6). */
const PATH_B_OVER_CAP: ProblemOverrides = {
  'too-many-records|422|P.4': { band: 'blocked', remedy: 'overCapPathB', detail: true },
}

export interface UploadStatusProps {
  state: UploadState
  /** The run's path: Path B's over-cap band counts base segments (owner decision 2026-09-26). */
  path?: RunPath
  onRetry: () => void
  onRemove: () => void
}

function RetryButton({ onRetry }: { onRetry: () => void }) {
  return (
    <Button variant="secondary" size="sm" onClick={onRetry}>
      Retry
    </Button>
  )
}

/** The file row and the bands for an upload in flight or refused; idle renders nothing. */
export function UploadStatus({ state, path = 'generate', onRetry, onRemove }: UploadStatusProps) {
  switch (state.kind) {
    case 'idle':
      return null
    case 'validating':
      return <FileRow filename={state.filename} sizeBytes={state.sizeBytes} onRemove={onRemove} />
    case 'validated':
      return (
        <FileRow
          filename={state.upload.filename}
          sizeBytes={state.upload.size_bytes}
          recordCount={state.upload.record_count}
          onRemove={onRemove}
        />
      )
    case 'retry':
      return (
        <div className="flex flex-col gap-2">
          <FileRow filename={state.filename} sizeBytes={state.sizeBytes} onRemove={onRemove} />
          <Band kind="info" action={<RetryButton onRetry={onRetry} />}>
            {state.detail}
          </Band>
        </div>
      )
    case 'overCap':
      // P.4: register 3 from the problem's members, the detail beneath, and Remove.
      return (
        <div className="flex flex-col gap-2">
          <ProblemBand problem={state.problem} override={path === 'analyze' ? PATH_B_OVER_CAP : undefined} />
          <div>
            <Button variant="secondary" size="sm" onClick={onRemove}>
              Remove
            </Button>
          </div>
        </div>
      )
    case 'expired':
      return <Band kind="blocked" requirement="U.1">{copyText('uploadExpired')}</Band>
    case 'failed': {
      const failure = state.failure
      if (failure.reason === 'storage') {
        return (
          <Band kind="blocked" action={<RetryButton onRetry={onRetry} />}>
            {copyText('storageFailed')}
          </Band>
        )
      }
      if (failure.reason === 'layout-version') {
        return (
          <Band kind="blocked">
            {/* owner decision 2026-09-26 (decision 10): was the inline "Only v1 is accepted:". */}
            {copyText('layoutVersionUnsupported')} <Mono>{failure.accepted.join(', ')}</Mono>
          </Band>
        )
      }
      if (failure.reason === 'problem') {
        const p = failure.problem
        if (p.type.endsWith('/try-again') && p.detail) {
          return (
            <Band kind="info" action={<RetryButton onRetry={onRetry} />}>
              {p.detail}
            </Band>
          )
        }
        return <ProblemBand problem={p} override={UPLOAD_OVERRIDES} />
      }
      return <Band kind="blocked">{copyText('genericError')}</Band>
    }
  }
}
